import React, {useReducer, useContext} from "react";
import {pageData} from "Data";

const generateInitialProgress = (pageData) => {
  const indexedPageCount = pageData.filter(page => !page.hasOwnProperty("isNotIndexed")).length;
  return new Array(indexedPageCount).fill(false);
}

const progressInitial = {
  currentRealIndex: 0, // the "real index" of the current page - includes "skipped" pages. zero-based
  currentNavIndex: null, // the "nav index" for page navigation - 1-based - null if user is not in form pages or on unindexed page.
  currentPage: null, 
  currentProgress: generateInitialProgress(pageData), // an array where each element corresponds to whether that page index is completed or not.
  formComplete: false,
  realIndexes: pageData.map((page, index) => { // includes all pages in the form
    return {
      slug: page.slug,
      realIndex: index,
    }
  }),

  unIndexedPages: pageData.map((page, i) => { // includes only the pages with the "isNotIndexed" property
    return {
      slug: page.slug,
      realIndex: i,
      navIndex: i + 1,
    }
  }).filter(page => page.hasOwnProperty("isNotIndexed")),

  navIndexes: pageData // includes only the pages WITHOUT the "isNotIndexed" property
  .filter(page => !page.hasOwnProperty('isNotIndexed'))
  .map((page, index) => {
    return {
      slug: page.slug,
      navigationIndex: index + 1,
    }
  }), 
  nextPage: pageData[0].slug, // the slug for the next page. if null, button will be disabled
  prevPage: null, // the slug for the previous page. if null, button will be disabled.
}

const ProgressContext = React.createContext(progressInitial);

const progressReducer = (state, action) => {
  const {type, payload} = action;
  switch(type) {
    
    case "CHANGE_PAGE" :

      if(payload === "/") {
        return {
          ...state,
          currentRealIndex: 0,
          currentNavIndex: null,
          currentPage: null,
          nextPage: pageData[0].slug,
          prevPage: null
        }
      }

      const newRealIndex = state.realIndexes.find(page => page.slug === payload).realIndex;
      const nextPageSlug = state.realIndexes.find(page => page.realIndex === newRealIndex + 1)?.slug || null;
      const prevPageSlug = state.realIndexes.find(page => page.realIndex === newRealIndex - 1)?.slug || null;

      const newStateProperties = {
        currentRealIndex: newRealIndex,
        currentPage: state.realIndexes[newRealIndex].slug,
        nextPage: nextPageSlug,
        prevPage: prevPageSlug,
        currentNavIndex: state.navIndexes.find(page => page.slug === payload)?.navigationIndex || null,
      }

      if(state.unIndexedPages.find(page => page.slug === payload)) { 
        // if the current page is NOT indexed (skipped), the navIndex will become null.
        newStateProperties.currentNavIndex = null;
      }

      const combinedState = {
        ...state,
        ...newStateProperties
      };

      return combinedState;

    case "UPDATE_PROGRESS" :

      const newProgress = [...state.currentProgress];
      const {slug, isComplete} = payload;
      const index = state.navIndexes.indexOf(state.navIndexes.find(page => page.slug === slug));

      newProgress[index] = isComplete;

      return {
        ...state,
        currentProgress: newProgress,
        formComplete: newProgress.includes(false) ? false : true,
      }

    case "RESTORE_PROGRESS":

      const restoredProgress = [...state.currentProgress];
      const targetPageIndex = payload - 1;

      for (let i = 0; i < targetPageIndex && i < restoredProgress.length; i++) {
        restoredProgress[i] = true;
      }

      return {
        ...state,
        currentProgress: restoredProgress,
        formComplete: restoredProgress.includes(false) ? false : true,
      }

    default:
      throw new Error(`No case for type ${type} in progressReducer.`);
  }
}

const ProgressProvider = ({children}) => {
  const [state, dispatch] = useReducer(progressReducer, progressInitial);

  const changePage = (newPageSlug) => {
    dispatch({
      type: "CHANGE_PAGE",
      payload: newPageSlug,
    });
  };

  const updateProgress = (slug, value) => {
    dispatch({
      type: "UPDATE_PROGRESS",
      payload: {
        slug: slug,
        isComplete: value,
      },
    })
  }

  const restoreProgress = (currentPage) => {
    dispatch({
      type: "RESTORE_PROGRESS",
      payload: currentPage,
    })
  }

  const providerValue = {
    currentNavIndex: state.currentNavIndex, // current "navigation index" of the page (1-based)
    currentRealIndex: state.currentRealIndex, // current "real index" of the page (0-based)
    nextPage: state.nextPage,// the slug of the next page - null if not in form
    currentPage: state.currentPage, // slug of current page - null if not in form
    prevPage: state.prevPage, // the slug of the previous page - null if not in form
    currentProgress: state.currentProgress,
    formComplete: state.formComplete,
    navIndexes: state.navIndexes,
    realIndexes: state.realIndexes,
    changePage,
    updateProgress,
    restoreProgress,
  };

  return <ProgressContext.Provider value={providerValue}>{children}</ProgressContext.Provider>;
};

const useProgress = ()=>{
  const context = useContext(ProgressContext);
  if(context === undefined) {
    throw new Error("useProgress must be used with ProgressContext");
  }
  return context;
}



export {
  progressReducer,
  ProgressProvider,
  useProgress,
  ProgressContext,
}